import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
    faMoneyBillWave,
    faCalendarCheck,
    faDumbbell,
    faClock
} from "@fortawesome/free-solid-svg-icons";
import { Col, Row, Card, ListGroup, OverlayTrigger, Tooltip } from "react-bootstrap";
import { jwtDecode } from "jwt-decode";

import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";

import moment from "moment";
import "moment/locale/pt";
moment.locale("pt");

const StatWidget = ({ icon, title, value }) => (
    <Col xs={4} xl={4} className="mb-4">
        <Card border="light" className="shadow-sm h-100">
            <Card.Body style={{ padding: "14px 12px" }}>
                <div className="d-flex flex-column align-items-center text-center gap-2">
                    <div
                        className="icon icon-shape icon-md rounded"
                        style={{ backgroundColor: "#1e1d45", color: "white", flexShrink: 0 }}
                    >
                        <FontAwesomeIcon icon={icon} />
                    </div>
                    <div>
                        <div className="fw-bold" style={{ fontSize: 20, color: "#1e1d45", lineHeight: 1 }}>{value}</div>
                        <div className="text-muted" style={{ fontSize: 11, marginTop: 3, lineHeight: 1.2 }}>{title}</div>
                    </div>
                </div>
            </Card.Body>
        </Card>
    </Col>
);

export default function UserDashboard() {
    const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

    const [userName, setUserName] = useState("Utilizador");
    const [stats, setStats] = useState({
        proximaAula: "-",
        pagamentosPendentes: 0,
        eventosMes: 0
    });
    const [events, setEvents] = useState([]);
    const [upcomingEvents, setUpcomingEvents] = useState([]);

    useEffect(() => {
        const loadDashboard = async () => {
            const token = localStorage.getItem("token") || sessionStorage.getItem("token");
            if (!token) return;

            const headers = { Authorization: `Bearer ${token}` };

            try {
                const decoded = jwtDecode(token);
                setUserName(
                    decoded["http://schemas.xmlsoap.org/ws/2005/05/identity/claims/name"] || "Utilizador"
                );

                // =========================
                // FETCH DE TODOS OS DADOS
                // =========================
                const [
                    aulasRes,
                    eventosRes,
                    planosRes,
                    turmasRes,
                    turmasAulaRes,
                    pagamentosRes
                ] = await Promise.all([
                    fetch(`${API_BASE_URL}/Aulas`, { headers }),
                    fetch(`${API_BASE_URL}/Eventos`, { headers }),
                    fetch(`${API_BASE_URL}/PlanoAulas`, { headers }),
                    fetch(`${API_BASE_URL}/Turmas`, { headers }),
                    fetch(`${API_BASE_URL}/TurmasAulas`, { headers }),
                    fetch(`${API_BASE_URL}/PagamentoPraticantes`, { headers })
                ]);

                const aulas = aulasRes.ok ? await aulasRes.json() : [];
                const eventos = eventosRes.ok ? await eventosRes.json() : [];
                const planos = planosRes.ok ? await planosRes.json() : [];
                const turmas = turmasRes.ok ? await turmasRes.json() : [];
                const turmasAula = turmasAulaRes.ok ? await turmasAulaRes.json() : [];
                const pagamentos = pagamentosRes.ok ? await pagamentosRes.json() : [];

                const now = moment();

                // =========================
                // 🔥 PRÓXIMA AULA
                // =========================
                const aulasFuturas = aulas
                    .map(a => ({
                        ...a,
                        dataHora: moment(`${moment(a.data).format("YYYY-MM-DD")} ${a.hora_inicio}`)
                    }))
                    .filter(a => a.dataHora.isAfter(now))
                    .sort((a, b) => a.dataHora - b.dataHora);

                let proximaAulaTexto = "Sem aulas";

                if (aulasFuturas.length > 0) {
                    const dataAula = aulasFuturas[0].dataHora;

                    if (dataAula.isSame(now, "day")) {
                        proximaAulaTexto = "Hoje";
                    } else if (dataAula.isSame(moment().add(1, "day"), "day")) {
                        proximaAulaTexto = "Amanhã";
                    } else if (dataAula.isSame(now, "week")) {
                        proximaAulaTexto = dataAula.format("dddd");
                    } else {
                        proximaAulaTexto = dataAula.format("DD/MM/YYYY");
                    }
                }

                // =========================
                // 💰 PAGAMENTOS PENDENTES
                // =========================
                const pagamentosPendentes = pagamentos.filter(p => p.isPago === 0).length;

                // =========================
                // 📅 EVENTOS 30 DIAS
                // =========================
                const daqui30 = moment().add(30, "days");

                const eventosMes = eventos.filter(e => {
                    const dataInicio = moment(e.data_inicio);
                    return dataInicio.isBetween(now, daqui30, null, "[]");
                }).length;

                setStats({
                    proximaAula: proximaAulaTexto,
                    pagamentosPendentes,
                    eventosMes
                });

                // =========================
                // CALENDÁRIO
                // =========================
                const calendarEvents = [];

                aulas.forEach(aula => {
                    const turmaRegs = turmasAula.filter(ta => ta.AulaId === aula.id);
                    const turmaIds = turmaRegs.map(tr => tr.TurmaId);
                    const turmaNomes = turmaIds
                        .map(id => turmas.find(t => t.id === id)?.turma)
                        .filter(Boolean);

                    calendarEvents.push({
                        id: `aula-${aula.id}`,
                        title: `Aula${turmaNomes.length ? " - " + turmaNomes.join(", ") : ""}`,
                        start: `${moment(aula.data).format("YYYY-MM-DD")}T${aula.hora_inicio}`,
                        end: `${moment(aula.data).format("YYYY-MM-DD")}T${aula.hora_fim}`,
                        backgroundColor: "#1e1d45",
                        borderColor: "#1e1d45",
                        extendedProps: {
                            tipo: "aula",
                            plano: planos.find(p => p.aulaId === aula.id),
                            turmas: turmaNomes
                        }
                    });
                });

                eventos.forEach(ev => {
                    calendarEvents.push({
                        id: `evento-${ev.id}`,
                        title: ev.nome,
                        start: ev.data_inicio,
                        end: ev.data_fim,
                        backgroundColor: "#E4B428",
                        borderColor: "#E4B428",
                        extendedProps: { tipo: "evento", descricao: ev.descricao }
                    });
                });

                setEvents(calendarEvents);

                // =========================
                // PRÓXIMOS EVENTOS (LATERAL)
                // =========================
                setUpcomingEvents(
                    eventos
                        .filter(e => moment(e.data_inicio).isSameOrAfter(now))
                        .sort((a, b) => moment(a.data_inicio) - moment(b.data_inicio))
                );

            } catch (err) {
                console.error("Erro no dashboard do utilizador:", err);
            }
        };

        loadDashboard();
    }, []);

    // Tooltip com info detalhada
    const renderEventContent = (eventInfo) => {
        const { tipo, plano, descricao, turmas } = eventInfo.event.extendedProps;

        return (
            <OverlayTrigger
                placement="top"
                overlay={
                    <Tooltip>
                        <strong>{eventInfo.event.title}</strong>
                        {tipo === "aula" && turmas && turmas.length > 0 && (
                            <>
                                <hr />
                                <div><em>Turmas:</em> {turmas.join(", ")}</div>
                            </>
                        )}
                        {tipo === "aula" && plano && (
                            <>
                                <hr />
                                <div><em>Plano:</em></div>
                                <div>{plano.sumario || "Sem sumário"}</div>
                            </>
                        )}
                        {tipo === "evento" && descricao && (
                            <>
                                <hr />
                                <div>{descricao}</div>
                            </>
                        )}
                    </Tooltip>
                }
            >
                <div style={{ fontWeight: 600, fontSize: 13 }}>{eventInfo.event.title}</div>
            </OverlayTrigger>
        );
    };

    return (
        <>
            <div className="py-4">
                <h1 className="font-anton">Olá, {userName}</h1>
            </div>

            <Row>
                <StatWidget
                    icon={faClock}
                    title="Próxima Aula"
                    value={stats.proximaAula}
                />

                <StatWidget
                    icon={faMoneyBillWave}
                    title="Pagamentos Pendentes"
                    value={stats.pagamentosPendentes}
                />

                <StatWidget
                    icon={faCalendarCheck}
                    title="Eventos (30 dias)"
                    value={stats.eventosMes}
                />
            </Row>

            <Row>
                <Col xs={12} xl={8} className="mb-4">
                    <Card border="light" className="shadow-sm">
                        <Card.Header>
                            <h5 className="mb-0">Calendário de Aulas e Eventos</h5>
                        </Card.Header>
                        <Card.Body className="p-2 p-md-3">
                            <FullCalendar
                                plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
                                initialView="dayGridMonth"
                                headerToolbar={{
                                    left: "prev,next",
                                    center: "title",
                                    right: "today"
                                }}
                                footerToolbar={{
                                    center: "dayGridMonth,timeGridWeek,timeGridDay"
                                }}
                                events={events}
                                editable={false}
                                selectable={false}
                                height="auto"
                                locale="pt"
                                eventContent={renderEventContent}
                            />
                        </Card.Body>
                    </Card>
                </Col>

                <Col xs={12} xl={4} className="mb-4">
                    <Card border="light" className="shadow-sm">
                        <Card.Header>
                            <h5 className="mb-0">Próximos Eventos</h5>
                        </Card.Header>
                        <Card.Body>
                            <ListGroup variant="flush">
                                {upcomingEvents.length > 0 ? upcomingEvents.map(ev => {
                                    const isProximo = moment(ev.data_inicio).diff(moment(), "days") <= 7;
                                    return (
                                        <ListGroup.Item key={ev.id} className="px-0 py-3" style={{ borderColor: "#f0f0f5" }}>
                                            <div className="d-flex align-items-center gap-3">
                                                <div style={{
                                                    minWidth: 44, width: 44, height: 44, borderRadius: 10,
                                                    background: isProximo ? "#1e1d45" : "#f0f0f5",
                                                    color: isProximo ? "white" : "#6c757d",
                                                    display: "flex", flexDirection: "column",
                                                    alignItems: "center", justifyContent: "center",
                                                    flexShrink: 0,
                                                }}>
                                                    <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", lineHeight: 1 }}>
                                                        {moment(ev.data_inicio).format("MMM")}
                                                    </span>
                                                    <span style={{ fontSize: 16, fontWeight: 900, lineHeight: 1 }}>
                                                        {moment(ev.data_inicio).format("DD")}
                                                    </span>
                                                </div>
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ fontWeight: 600, fontSize: 13, color: "#1e1d45", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                        {ev.nome}
                                                    </div>
                                                    {isProximo && (
                                                        <div style={{ fontSize: 11, color: "#0d6efd", fontWeight: 600 }}>
                                                            em {moment(ev.data_inicio).diff(moment(), "days")} dias
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        </ListGroup.Item>
                                    );
                                }) : (
                                    <p className="text-center text-muted py-3 mb-0">Não existem eventos.</p>
                                )}
                            </ListGroup>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>
        </>
    );
}